// Matches the fallback used in voiceTwiml.ts for outbound VoIP from the
// desktop browser SDK — the default business caller ID when a call site
// doesn't have a specific selected number in scope (e.g. a plain "call this
// contact" button outside the softphone dialer).
const DEFAULT_BUSINESS_NUMBER = '+14256002624';

function normalizePhoneNumber(phoneNumber: string): string {
  const digits = phoneNumber.replace(/\D/g, '');
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
  return phoneNumber.trim();
}

export interface PlaceCallContactInfo {
  contactName?: string;
  contactPicture?: string;
}

/**
 * Places an outbound call, masking the agent's real number behind the
 * selected business number. VoIP-only (Twilio Voice SDK via
 * CapacitorTwilioVoice) — never falls back to a direct cellular tel: call,
 * since that would show the agent's real personal number to the person
 * being called with no way to mask it.
 */
export async function placePhoneCall(
  phoneNumber: string,
  businessNumber?: string,
  contact?: PlaceCallContactInfo,
): Promise<void> {
  const normalizedPhone = normalizePhoneNumber(phoneNumber);
  const callerId = businessNumber || DEFAULT_BUSINESS_NUMBER;

  const { Capacitor } = await import('@capacitor/core');
  if (!Capacitor.isNativePlatform()) {
    // Desktop/web already has its own VoIP path via useIvrDevice/device.connect().
    return;
  }

  const { CapacitorTwilioVoice } = await import('@capgo/capacitor-twilio-voice');
  // displayName/contactPicture aren't part of this plugin's own TS API on
  // Android — a local patch (see patches/@capgo+capacitor-twilio-voice) reads
  // them and forwards them to the native outgoing-call screen so it can show
  // the contact being called instead of the app's own business branding.
  await CapacitorTwilioVoice.makeCall({
    to: normalizedPhone,
    callerId,
    displayName: contact?.contactName,
    ...(contact?.contactPicture ? { contactPicture: contact.contactPicture } as Record<string, unknown> : {}),
  });
}
